import React, { useEffect, useState } from "react";
import { Loader2, Save, Ticket } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Checkbox } from "@cms/ui/components/checkbox";
import { fetchEventBookingConfig, saveEventBookingConfig } from "../lib/booking";
import type { ApiClient } from "../lib/api";

/**
 * The event editor's Bookings panel (events × booking module integration):
 * a per-event enable toggle and places capacity. Render it only when the
 * booking module is active — the parent owns that check.
 */
export function EventBookingCard({ api, eventId }: { api: ApiClient | null; eventId: string }) {
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [capacity, setCapacity] = useState("20");
  const [bookedPlaces, setBookedPlaces] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    fetchEventBookingConfig(api, eventId)
      .then(({ config, booked_places }) => {
        if (cancelled) return;
        if (config) {
          setEnabled(config.enabled === 1);
          setCapacity(String(config.capacity));
        }
        setBookedPlaces(booked_places);
        setLoaded(true);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => { cancelled = true; };
  }, [api, eventId]);

  const save = async () => {
    if (!api) return;
    const places = Number(capacity);
    if (!Number.isInteger(places) || places < 1) {
      setError("Capacity must be at least 1.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await saveEventBookingConfig(api, eventId, { enabled, capacity: places });
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Ticket className="h-4 w-4 text-muted-foreground" />
          Bookings
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!loaded && !error && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {loaded && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={enabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEnabled(e.target.checked); setSaved(false); }}
              />
              Customers can book places for this event
            </label>
            <div className="space-y-1.5">
              <Label htmlFor="evt-capacity">Places per date</Label>
              <Input
                id="evt-capacity" type="number" min={1} value={capacity} className="w-32"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setCapacity(e.target.value); setSaved(false); }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {bookedPlaces > 0
                ? `${bookedPlaces} place${bookedPlaces === 1 ? "" : "s"} booked across upcoming dates — see the Bookings calendar.`
                : "The event must be live for customers to book it."}
            </p>
            <Button size="sm" onClick={() => { void save(); }} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saved ? "Saved" : "Save booking settings"}
            </Button>
          </div>
        )}
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
